// frontend/src/pages/Auth/Login.jsx - VERSIÓN FINAL

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Button,
  Alert,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';

// ✅ Importar el logo
import masLogo from '../../assets/img/maslogosinletras.png';

const GOOGLE_CLIENT_ID = '698693339809-5m362dj7lnaftjt59p03ie0bb5oeurnd.apps.googleusercontent.com';

const Login = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    phone: '',
  });

  const { loginWithGoogle, loginWithEmail, registerWithEmail } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const result = await loginWithGoogle(credentialResponse.credential);

      if (result.status === 'pending') {
        navigate('/pending-approval', { replace: true });
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error en login con Google:', err);
      const errorMsg = err.response?.data?.detail || 
                      err.response?.data?.error ||
                      'Error al iniciar sesión con Google.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Error de Google OAuth:', error);
    setError('Error al conectar con Google. Verifica que hayas autorizado la aplicación.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (activeTab === 'login') {
        const result = await loginWithEmail(formData.email, formData.password);
        
        if (result.status === 'pending') {
          navigate('/pending-approval', { replace: true });
        } else {
          navigate('/admin/dashboard', { replace: true });
        }
      } else {
        if (formData.password !== formData.password_confirm) {
          setError('Las contraseñas no coinciden');
          setLoading(false);
          return;
        }

        await registerWithEmail(formData);
        navigate('/pending-approval', { replace: true });
      }
    } catch (err) {
      console.error('Error:', err);
      const errorMsg = err.response?.data?.detail || 
                      err.response?.data?.message ||
                      (activeTab === 'login' ? 'Credenciales inválidas' : 'Error al crear la cuenta');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div 
        className="min-vh-100 d-flex align-items-center position-relative overflow-hidden" 
        style={{ 
          background: 'linear-gradient(135deg, #038D81 0%, #05C9B4 100%)'
        }}
      >
        {/* ✅ Decoración de fondo */}
        <div 
          className="position-absolute w-100 h-100" 
          style={{ 
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }}
        />

        <Container className="position-relative">
          <Row className="justify-content-center">
            <Col lg="5" md="7">
              <Card className="border-0 mb-0 shadow-lg">
                <CardBody className="px-lg-5 py-lg-5">
                  {/* ✅ Header con Logo MAS+ */}
                  <div className="text-center mb-4">
                    <div className="mb-3">
                      <img 
                        src={masLogo} 
                        alt="MAS+ Logo" 
                        style={{ 
                          height: '80px',
                          width: 'auto',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))'
                        }}
                      />
                    </div>
                    <h3 className="text-dark font-weight-bold">Consultoría MÁS+</h3>
                    <p className="text-muted mb-0">Sistema de Gestión Empresarial</p>
                  </div>

                  {/* Tabs */}
                  <Nav tabs className="nav-fill mb-4">
                    <NavItem>
                      <NavLink
                        className={`
                          ${activeTab === 'login' ? 'active' : ''}
                          cursor-pointer text-dark fw-bold
                        `}
                        onClick={() => {
                          setActiveTab('login');
                          setError('');
                        }}
                        style={{ 
                          cursor: 'pointer',
                          borderRadius: '0.5rem 0.5rem 0 0',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <i className="ni ni-key-25 me-2"></i>
                        Iniciar Sesión
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={`${activeTab === 'register' 
                          ? 'active bg-gradient-primary text-white'           // ← aquí text-white
                          : 'text-dark'} cursor-pointer`}
                        onClick={() => {
                          setActiveTab('register');
                          setError('');
                        }}
                        style={{ 
                          cursor: 'pointer',
                          borderRadius: '0.5rem 0.5rem 0 0',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <i className="ni ni-circle-08 me-2"></i>
                        Registrarse
                      </NavLink>
                    </NavItem>
                  </Nav>

                  {/* Error Alert */}
                  {error && (
                    <Alert color="danger" className="alert-dismissible fade show">
                      <span className="alert-icon">
                        <i className="ni ni-fat-remove"></i>
                      </span>
                      <span className="alert-text ms-2">{error}</span>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError('')}
                        aria-label="Close"
                      />
                    </Alert>
                  )}

                  {/* Google Login Button */}
                  <div className="mb-3">
                    {loading ? (
                      <Button color="primary" size="lg" block disabled className="shadow">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Procesando...
                      </Button>
                    ) : (
                      <div className="d-grid">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          useOneTap={false}
                          theme="outline"
                          size="large"
                          text={activeTab === 'login' ? "signin_with" : "signup_with"}
                          shape="rectangular"
                          width="100%"
                        />
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="text-center my-3">
                    <div className="d-flex align-items-center">
                      <hr className="flex-grow-1" />
                      <small className="text-muted mx-3">O continúa con email</small>
                      <hr className="flex-grow-1" />
                    </div>
                  </div>

                  {/* Form */}
                  <Form onSubmit={handleSubmit}>
                    {activeTab === 'register' && (
                      <>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <label className="form-control-label text-dark">
                                <i className="ni ni-single-02 me-1"></i>
                                Nombre *
                              </label>
                              <Input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                required
                                placeholder="Juan"
                                className="form-control-alternative"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <label className="form-control-label text-dark">
                                <i className="ni ni-single-02 me-1"></i>
                                Apellido *
                              </label>
                              <Input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                required
                                placeholder="Pérez"
                                className="form-control-alternative"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <FormGroup>
                          <label className="form-control-label text-dark">
                            <i className="ni ni-mobile-button me-1"></i>
                            Teléfono
                          </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="1234567890"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </>
                    )}

                    <FormGroup>
                      <label className="form-control-label text-dark">
                        <i className="ni ni-email-83 me-1"></i>
                        Correo Electrónico *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="tu@email.com"
                        className="form-control-alternative"
                      />
                    </FormGroup>

                    <FormGroup>
                      <label className="form-control-label text-dark">
                        <i className="ni ni-lock-circle-open me-1"></i>
                        Contraseña *
                      </label>
                      <Input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        minLength="8"
                        placeholder="••••••••"
                        className="form-control-alternative"
                      />
                      {activeTab === 'register' && (
                        <small className="text-muted">
                          <i className="ni ni-check-bold me-1"></i>
                          Mínimo 8 caracteres
                        </small>
                      )}
                    </FormGroup>

                    {activeTab === 'register' && (
                      <FormGroup>
                                                <label className="form-control-label text-dark">
                          <i className="ni ni-lock-circle-open me-1"></i>
                          Confirmar Contraseña *
                        </label>
                        <Input
                          type="password"
                          name="password_confirm"
                          value={formData.password_confirm}
                          onChange={handleInputChange}
                          required
                          minLength="8"
                          placeholder="••••••••"
                          className="form-control-alternative"
                        />
                      </FormGroup>
                    )}

                    {activeTab === 'login' && (
                      <div className="text-end mb-3">
                        <a 
                          href="#!" 
                          className="text-primary text-sm" 
                          onClick={(e) => e.preventDefault()}
                        >
                          <i className="ni ni-key-25 me-1"></i>
                          ¿Olvidaste tu contraseña?
                        </a>
                      </div>
                    )}

                    <Button
                      color="primary"
                      size="lg"
                      block
                      type="submit"
                      disabled={loading}
                      className="shadow-lg"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <i className={`ni ${activeTab === 'login' ? 'ni-key-25' : 'ni-circle-08'} me-2`}></i>
                          {activeTab === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
                        </>
                      )}
                    </Button>
                  </Form>

                  {/* Footer Info */}
                  <div className="text-center mt-4">
                    <div className="d-flex justify-content-center gap-3 mb-3">
                      <small className="text-muted">
                        <i className="ni ni-lock-circle-open me-1 text-success"></i>
                        Conexión segura
                      </small>
                      {activeTab === 'register' && (
                        <small className="text-muted">
                          <i className="ni ni-check-bold me-1 text-primary"></i>
                          Revisión de admin
                        </small>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Copyright */}
              <div className="text-center mt-4">
                <p className="text-white text-sm mb-0 font-weight-bold">
                  © 2024 Consultoría MAS+. Todos los derechos reservados.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </GoogleOAuthProvider>
  );
};

export default Login;

